import Quill from "quill";
import MagicUrl from "quill-magic-url";
import QuillTableBetter from "quill-table-better";
import "quill-table-better/dist/quill-table-better.css";
import "quill/dist/quill.snow.css";
import React, { useEffect, useState } from "react"; // React をインポート
import axios from "axios";
// import Cookies from "js-cookie"; // 未使用のためコメントアウト
import { InstagramEmbedBlot } from "./InstagramEmbed";
import { LinkCardBlot } from "./LinkCard";
import table_japanese from "./table_japanese";
import { TwitterEmbedBlot } from "./TwitterEmbed";
import { VideoEmbedBlot } from "./VideoEmbed";
import { useFileContext } from "../../../../context/FileContext";
import { apiRequest } from "../../../../utils/api";

interface QuillInterface extends Quill {
  import: (path: string) => any;
}

interface QuillInitializerProps {
  fileUuid: string | undefined;
  lang: string;
  setQuill: (quill: Quill | null) => void;
  quillRef: React.MutableRefObject<Quill | null>;
  onInitialized: (quillInstance: Quill) => void;
}

const QuillInitializerComponent = ({ fileUuid, lang, setQuill, quillRef, onInitialized }: QuillInitializerProps) => {
  const {
    fileText: fileTextMap,
    setFileText: contextSetFileText,
    setFileTitle: contextSetFileTitle,
    setTags: contextSetTags,
    setLanguage: contextSetLanguage,
    setPublicStatus: contextSetPublicStatus,
    setPublicUuid: contextSetPublicUuid,
    setThumbnailImage: contextSetThumbnailImage,
  } = useFileContext();

  const isNewFile = fileUuid === "new";
  const [isInitialized, setIsInitialized] = useState(false);

  // マークダウンをHTMLに変換する関数
  const convertMarkdownToHtml = async (markdownContent: string): Promise<string> => {
    try {
      const response = await apiRequest<{ html: string }>("/api/v1/text-file/markdown-to-html", "POST", {
        content: markdownContent,
      });
      return response.data.html;
    } catch (error) {
      console.error("Failed to convert markdown to HTML:", error);
      return markdownContent; // 変換に失敗した場合は元のテキストをそのまま返す
    }
  };

  // ファイル取得 (TextEditor.tsxから移動)
  const getTextFile = async () => {
    try {
      if (!fileUuid) return;
      const res = await axios.get("/api/v1/text-file/" + fileUuid);
      if (res.data) {
        contextSetFileTitle({ uuid: fileUuid!, title: res.data.title });
        contextSetTags({ uuid: fileUuid!, tags: res.data.tags });
        contextSetLanguage({ uuid: fileUuid!, language: res.data.language || lang });
        contextSetPublicStatus({ uuid: fileUuid!, status: res.data.public_status });
        contextSetPublicUuid({ uuid: fileUuid!, publicUuid: res.data.public_uuid });
        contextSetThumbnailImage({ uuid: fileUuid!, url: res.data.thumbnail });
        return res.data;
      }
    } catch (error) {
      console.error("An unknown error occurred in getTextFile:", error);
    }
  };

  // 新しいファイルの場合にfileTextMapの変更を監視してQuillの内容を更新
  useEffect(() => {
    if (isNewFile && quillRef.current && fileUuid && !isInitialized) {
      const initialContent = fileTextMap[fileUuid];
      if (initialContent && quillRef.current.root) {
        setIsInitialized(true);
        // マークダウンをHTMLに変換してから挿入
        convertMarkdownToHtml(initialContent).then((htmlContent) => {
          if (quillRef.current && quillRef.current.root) {
            quillRef.current.root.innerHTML = htmlContent;
            setTimeout(() => {
              if (quillRef.current) {
                quillRef.current.history.clear();
              }
            });
          }
        });
      }
    }
  }, [fileTextMap, fileUuid, isNewFile, isInitialized]);

  useEffect(() => {
    try {
      const editor = document.getElementById("editor");
      if (editor && !quillRef.current) {
        Quill.debug("error");
        const QuillClass = Quill as unknown as QuillInterface;
        const ImageFormat = QuillClass.import("formats/image");

        class ImageBlot extends ImageFormat {
          static create(value: { src?: string; class?: string; id?: string }) {
            // anyを具体的な型に変更
            const node = super.create();
            if (value && value.src) {
              node.setAttribute("src", value.src);
              node.setAttribute("class", value.class);
              if (value.id) {
                node.setAttribute("data-id", value.id);
              }
            }
            return node;
          }
        }

        Quill.register("formats/image", ImageBlot, true);
        Quill.register("formats/link-card", LinkCardBlot, true);
        Quill.register("formats/video-embed", VideoEmbedBlot, true);
        Quill.register("formats/instagram-embed", InstagramEmbedBlot, true);
        Quill.register("formats/twitter-embed", TwitterEmbedBlot, true);
        Quill.register("modules/magicUrl", MagicUrl, true);
        Quill.register(
          {
            "modules/table-better": QuillTableBetter,
          },
          true
        );

        let l: string | { name: string; content: unknown } = "en_US";
        if (lang === "ja") {
          l = { name: "ja_JP", content: table_japanese };
        }

        const q = new Quill(editor, {
          theme: "snow",
          modules: {
            toolbar: "#toolbar",
            history: {
              delay: 2000,
              maxStack: 500,
              userOnly: true,
            },
            magicUrl: {
              urlRegularExpression: /(https?:\/\/[\S]+)|(www.[\S]+)|(tel:[\S]+)/g,
              globalRegularExpression: /(https?:\/\/|www\.|tel:)[\S]+/g,
            },
            table: false,
            "table-better": {
              language: l,
              menus: ["column", "row", "merge", "wrap", "delete"],
              toolbarTable: false,
            },
            keyboard: {
              bindings: {
                endKey: {
                  key: "End",
                  handler: function () {
                    const currentPosition = q.getSelection(true)?.index;
                    if (typeof currentPosition === "number") {
                      const [line, offset] = q.getLine(currentPosition);
                      if (line) {
                        const lineLength = line.length();
                        q.setSelection(currentPosition + lineLength - offset - 1, 0);
                        return false;
                      }
                    }
                    return true; // デフォルトの動作を許可
                  },
                },
                save: {
                  key: "s",
                  shortKey: true,
                  handler: function () {
                    // このコンポーネントからは直接トリガーしない
                    const event = new CustomEvent("quillSaveTrigger");
                    document.dispatchEvent(event);
                    return false;
                  },
                },
                customFunction: {
                  key: 191, // '/'のキーコード
                  altKey: true,
                  handler: function () {
                    const event = new CustomEvent("quillKreaVerseTrigger");
                    document.dispatchEvent(event);
                    return false;
                  },
                },
                ...QuillTableBetter.keyboardBindings,
              },
            },
          },
        });

        if (isNewFile) {
          // 新しいファイルの場合は何もしない（useEffectで処理される）
        } else {
          // 既存ファイルの場合はAPIから取得
          getTextFile().then((res) => {
            if (res) {
              if (q && q.root) {
                q.root.innerHTML = res.content;
                setTimeout(() => {
                  contextSetFileText({ uuid: fileUuid!, content: q.getText() });
                  q.history.clear();
                });
              }
            }
          });
        }
        setQuill(q);
        quillRef.current = q;
        onInitialized(q); // 初期化完了を通知
      }
    } catch (error) {
      console.error("An unknown error occurred during Quill initialization:", error);
    }
  }, [fileUuid, lang]);

  return null; // このコンポーネントはUIを持たない
};
export default React.memo(QuillInitializerComponent); // React.memoでラップ
